import React, { useState } from 'react';
import { NavBar } from '../components/NavBar';
import { ExtensionIcon } from '../components/ExtensionIcon';
import { Logo } from '../components/Logo';
import { ChatWidget } from '../components/ChatWidget';
import { FeedbackForm } from '../components/FeedbackForm';
import { BeforeAfterSlider, getExtensionHeroComponents } from './ExtensionAnimations';
import { PRODUCTS, productOfferTexts } from './products';
import { useIsMobile } from '../hooks/useIsMobile';
import { colors, spacing, typography, radius, brandGradient, cardStyle, pageStyle, } from '../theme';
// export, damit der Waechter extension-hero-map.test.ts die Deckung gegen
// PRODUCTS gegenrechnen kann: Diese Map ist die zweite Datenquelle der
// Produktseite, und eine fehlende Extension faellt hier genauso still aus wie
// in der HERO_MAP (WI#4521).
export const EXTENSION_DETAILS = {
    'p1-ado-filter': {
        heroSubtitle: 'Stop hunting through 4,000 backlog items. ADO Filter gives you AND/OR query logic, team presets, and a dynamic Board view �� all without leaving Azure DevOps.',
        useCases: [
            {
                icon: '🔍',
                problem: 'Finding specific items in a large backlog takes minutes',
                solution: 'Combine any field + operator + value with AND/OR logic. Results update instantly.',
            },
            {
                icon: '👥',
                problem: 'Each team member rebuilds the same filter from scratch',
                solution: 'Save and share filter presets across the team. One click restores any configuration.',
            },
            {
                icon: '📋',
                problem: 'The Board view doesn\'t reflect your Kanban column order',
                solution: 'Dynamic Board view that auto-matches your Kanban column ordering.',
            },
        ],
        howItWorks: [
            'Install from VS Marketplace — no server setup required',
            'Open any Backlog or Board view in Azure DevOps',
            'Click the Filter toolbar to build your query with AND/OR logic',
            'Save as a team preset for one-click recall',
        ],
    },
    'p2-work-item-freeze': {
        heroSubtitle: 'Protect your audit trail. Work Item Freeze locks approved work items by iteration, area path and type — with exception requests and a full audit log.',
        useCases: [
            {
                icon: '🔒',
                problem: 'Someone edited a closed Sprint item after the Sprint Review',
                solution: 'Freeze rules lock items automatically. A clear banner explains why editing is blocked.',
            },
            {
                icon: '📋',
                problem: 'Auditors demand an immutable change history for compliance',
                solution: 'Every exception request and approval is logged. Full audit trail in ADO.',
            },
            {
                icon: '🙋',
                problem: 'Legitimate fixes need to happen to a frozen item',
                solution: 'Exception request workflow: user requests, admin approves, time-limited unlock granted.',
            },
        ],
        howItWorks: [
            'Define freeze rules: iteration paths, area paths, work-item types',
            'Items matching the rules show a visual freeze banner',
            'Users submit exception requests — admins approve with one click',
            'All actions logged. Full audit trail available in the extension settings.',
        ],
        competitorNote: '★ The only work-item freeze extension on the VS Marketplace',
    },
    'p3-status-freeze': {
        heroSubtitle: 'Your process says Active → Testing → Done. Status Freeze makes ADO enforce it — blocking invalid state transitions in real-time.',
        useCases: [
            {
                icon: '🔄',
                problem: 'Stories jump from Active straight to Done, skipping Testing',
                solution: 'Define allowed transitions per work-item type. Invalid moves are blocked instantly.',
            },
            {
                icon: '📅',
                problem: 'PI-end: dozens of items need locking before the release window',
                solution: 'One-click PI-end preset. Lock all active items across the entire ART at once.',
            },
            {
                icon: '👑',
                problem: 'Release managers need bypass access for hotfixes',
                solution: 'Configure bypass groups via ADO Identity Picker. Authorized users always get through.',
            },
        ],
        howItWorks: [
            'Configure transition rules per work-item type in extension settings',
            'Status Freeze intercepts every state change in real-time',
            'Invalid transitions show a clear, customizable error message',
            'PI-end preset: single button locks changes across the entire iteration',
        ],
        competitorNote: '★ The only state-transition enforcement extension on the VS Marketplace',
    },
    'p4-iteration-checker': {
        heroSubtitle: 'Your Feature says Sprint 3 — its child Stories say Sprint 1, 2, and 4. Iteration Checker finds every inconsistency and fixes them in one click.',
        useCases: [
            {
                icon: '🌳',
                problem: 'SAFe hierarchy has items spread across wrong iterations',
                solution: 'Recursive scan across all hierarchy levels. Every mismatch flagged by severity.',
            },
            {
                icon: '⚡',
                problem: 'Sprint retrospective: 40 items need iteration corrections',
                solution: 'Batch auto-fix: select mismatched items, click Fix — done in seconds.',
            },
            {
                icon: '⏰',
                problem: 'Problems are discovered too late in stand-up',
                solution: 'Scheduled daily scan via Azure Function. Issues emailed before morning stand-up.',
            },
        ],
        howItWorks: [
            'Run manual scan or schedule daily automated checks via Azure Function',
            'Results grouped by severity: Critical, Warning, Info',
            'Select items for batch correction — or let auto-fix handle everything',
            'Scan history retained for trend analysis',
        ],
    },
    'p5-multi-level-query': {
        heroSubtitle: 'Query Epics with their child Features and Stories in a single result. Multi-Level Query traverses your entire SAFe hierarchy — something ADO can\'t do natively.',
        useCases: [
            {
                icon: '🔗',
                problem: 'ADO queries only return direct children, not the full hierarchy',
                solution: 'Multi-Level Query traverses Epic → Feature → Story → Task in one operation.',
            },
            {
                icon: '📊',
                problem: 'Program-level reporting requires 5 separate WIQL queries + manual merge',
                solution: 'One query returns the complete tree. Export to CSV or display in a hierarchy view.',
            },
            {
                icon: '🎯',
                problem: 'PI Planning: need all Stories linked to a specific Feature',
                solution: 'Filter by any level, get the full subtree. Perfect for PI Planning visibility.',
            },
        ],
        howItWorks: [
            'Open Multi-Level Query from the ADO sidebar',
            'Select the root level: Epic, Feature, or Story',
            'Define filters at any hierarchy level',
            'Results display as a collapsible tree or flat list. Export to CSV.',
        ],
    },
};
// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
const ExtensionHero = ({ product, detail, }) => {
    const heroComponents = getExtensionHeroComponents(product.id);
    return (React.createElement("section", { style: {
            background: `linear-gradient(160deg, ${colors.dark} 0%, #1E293B 100%)`,
            padding: `${spacing.xxl}px ${spacing.xl}px`,
        } },
        React.createElement("div", { style: { maxWidth: 1040, margin: '0 auto', display: 'flex', gap: spacing.xl, alignItems: 'center', flexWrap: 'wrap' } },
            React.createElement("div", { style: { flex: '1 1 400px', display: 'flex', flexDirection: 'column', gap: spacing.lg } },
                React.createElement("a", { href: "/", style: {
                        color: colors.accent,
                        textDecoration: 'none',
                        fontSize: typography.fontSizeSm,
                        fontFamily: typography.fontFamily,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: spacing.xs,
                    } }, "\u2190 All extensions"),
                React.createElement("div", { style: { display: 'flex', alignItems: 'center', gap: spacing.lg, flexWrap: 'wrap' } },
                    React.createElement(ExtensionIcon, { extId: product.id, abbr: product.abbr, size: 72 }),
                    React.createElement("div", null,
                        React.createElement("h1", { style: {
                                margin: 0,
                                fontSize: typography.fontSizeXl,
                                fontWeight: 800,
                                fontFamily: typography.fontFamily,
                                color: colors.white,
                                lineHeight: 1.2,
                            } }, product.name),
                        React.createElement("p", { style: {
                                margin: `${spacing.xs}px 0 0 0`,
                                fontSize: typography.fontSizeBase,
                                color: '#94A3B8',
                                fontFamily: typography.fontFamily,
                            } }, product.tagline))),
                React.createElement("p", { style: {
                        margin: 0,
                        fontSize: typography.fontSizeMd,
                        color: '#CBD5E1',
                        fontFamily: typography.fontFamily,
                        lineHeight: 1.7,
                        maxWidth: 660,
                    } }, detail.heroSubtitle),
                detail.competitorNote && (React.createElement("p", { style: {
                        margin: 0,
                        fontSize: typography.fontSizeSm,
                        color: colors.accent,
                        fontFamily: typography.fontFamily,
                        fontWeight: 600,
                    } }, detail.competitorNote)),
                React.createElement("div", { style: { display: 'flex', gap: spacing.md, flexWrap: 'wrap', alignItems: 'center' } },
                    React.createElement("a", { href: product.marketplaceUrl, target: "_blank", rel: "noopener noreferrer", style: {
                            display: 'inline-block',
                            padding: `${spacing.sm + 2}px ${spacing.xl}px`,
                            background: brandGradient,
                            color: colors.white,
                            textDecoration: 'none',
                            borderRadius: radius.button,
                            fontSize: typography.fontSizeBase,
                            fontFamily: typography.fontFamily,
                            fontWeight: 700,
                            boxShadow: `0 4px 16px ${colors.primary}55`,
                        }, "data-testid": "extension-install-cta" }, productOfferTexts(product.offer).installCta),
                    React.createElement("span", { style: {
                            fontSize: typography.fontSizeSm,
                            color: '#64748B',
                            fontFamily: typography.fontFamily,
                        }, "data-testid": "extension-offer-badge" }, productOfferTexts(product.offer).badge))),
            heroComponents && (React.createElement("div", { style: {
                    flex: '1 1 300px',
                    maxWidth: 420,
                    borderRadius: 12,
                    overflow: 'hidden',
                    position: 'relative',
                    aspectRatio: '16/9',
                    minHeight: 180,
                } },
                React.createElement(heroComponents.Hero, null))))));
};
// ---------------------------------------------------------------------------
// Use Cases
// ---------------------------------------------------------------------------
const UseCasesSection = ({ detail }) => (React.createElement("section", { style: { padding: `${spacing.xxl}px ${spacing.xl}px`, backgroundColor: colors.white } },
    React.createElement("div", { style: { maxWidth: 800, margin: '0 auto' } },
        React.createElement("h2", { style: {
                margin: `0 0 ${spacing.xl}px 0`,
                fontSize: typography.fontSizeLg,
                fontWeight: 800,
                fontFamily: typography.fontFamily,
                color: colors.textPrimary,
                textAlign: 'center',
            } }, "Problems It Solves"),
        React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: spacing.md } }, detail.useCases.map((uc, i) => (React.createElement("div", { key: i, style: {
                ...cardStyle,
                display: 'grid',
                gridTemplateColumns: '48px 1fr',
                gap: spacing.md,
                alignItems: 'start',
            } },
            React.createElement("span", { style: { fontSize: 32, lineHeight: 1 } }, uc.icon),
            React.createElement("div", null,
                React.createElement("p", { style: {
                        margin: `0 0 ${spacing.xs}px 0`,
                        fontSize: typography.fontSizeSm,
                        color: colors.error,
                        fontFamily: typography.fontFamily,
                        lineHeight: 1.5,
                    } },
                    "\u2717 ",
                    uc.problem),
                React.createElement("p", { style: {
                        margin: 0,
                        fontSize: typography.fontSizeSm,
                        color: colors.success,
                        fontFamily: typography.fontFamily,
                        fontWeight: 600,
                        lineHeight: 1.5,
                    } },
                    "\u2192 ",
                    uc.solution)))))))));
// ---------------------------------------------------------------------------
// Features
// ---------------------------------------------------------------------------
const FeaturesSection = ({ product }) => (React.createElement("section", { style: { padding: `${spacing.xxl}px ${spacing.xl}px`, backgroundColor: colors.lightBg } },
    React.createElement("div", { style: { maxWidth: 800, margin: '0 auto' } },
        React.createElement("h2", { style: {
                margin: `0 0 ${spacing.xl}px 0`,
                fontSize: typography.fontSizeLg,
                fontWeight: 800,
                fontFamily: typography.fontFamily,
                color: colors.textPrimary,
                textAlign: 'center',
            } }, "What's Included"),
        React.createElement("div", { style: {
                width: '100%',
                height: 280,
                borderRadius: radius.card,
                backgroundColor: colors.white,
                border: `1px dashed ${colors.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: spacing.xl,
                overflow: 'hidden',
            } },
            React.createElement("img", { src: product.screenshotPlaceholder, alt: `${product.name} screenshot`, style: { width: '100%', height: '100%', objectFit: 'cover' }, onError: (e) => {
                    const t = e.currentTarget;
                    t.style.display = 'none';
                    const parent = t.parentElement;
                    if (parent) {
                        parent.innerHTML = `<span style="font-family:${typography.fontFamily};font-size:${typography.fontSizeSm}px;color:${colors.textMuted}">Screenshot coming soon</span>`;
                    }
                } })),
        React.createElement("div", { style: {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                gap: spacing.md,
            } }, product.features.map((feature) => (React.createElement("div", { key: feature, style: {
                ...cardStyle,
                display: 'flex',
                alignItems: 'flex-start',
                gap: spacing.sm,
            } },
            React.createElement("span", { style: { color: colors.success, fontWeight: 700, flexShrink: 0, lineHeight: 1.5 } }, "\u2713"),
            React.createElement("span", { style: {
                    fontSize: typography.fontSizeSm,
                    fontFamily: typography.fontFamily,
                    color: colors.textSecondary,
                    lineHeight: 1.5,
                } }, feature))))))));
// ---------------------------------------------------------------------------
// How It Works
// ---------------------------------------------------------------------------
const HowItWorksSection = ({ detail }) => (React.createElement("section", { style: { padding: `${spacing.xxl}px ${spacing.xl}px`, backgroundColor: colors.white } },
    React.createElement("div", { style: { maxWidth: 800, margin: '0 auto' } },
        React.createElement("h2", { style: {
                margin: `0 0 ${spacing.xl}px 0`,
                fontSize: typography.fontSizeLg,
                fontWeight: 800,
                fontFamily: typography.fontFamily,
                color: colors.textPrimary,
                textAlign: 'center',
            } }, "How It Works"),
        React.createElement("div", { style: { display: 'flex', flexDirection: 'column', gap: spacing.sm } }, detail.howItWorks.map((step, i) => (React.createElement("div", { key: i, style: {
                display: 'flex',
                alignItems: 'flex-start',
                gap: spacing.md,
            } },
            React.createElement("div", { style: {
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: brandGradient,
                    color: colors.white,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: typography.fontSizeSm,
                    fontFamily: typography.fontFamily,
                    fontWeight: 700,
                    flexShrink: 0,
                } }, i + 1),
            React.createElement("p", { style: {
                    margin: 0,
                    fontSize: typography.fontSizeBase,
                    fontFamily: typography.fontFamily,
                    color: colors.textSecondary,
                    lineHeight: 1.6,
                    paddingTop: 3,
                } }, step))))))));
// ---------------------------------------------------------------------------
// CTA
// ---------------------------------------------------------------------------
const CtaSection = ({ product }) => (React.createElement("section", { style: {
        padding: `${spacing.xxl}px ${spacing.xl}px`,
        background: `linear-gradient(160deg, ${colors.dark} 0%, #1E293B 100%)`,
        textAlign: 'center',
    } },
    React.createElement("div", { style: { maxWidth: 560, margin: '0 auto' } },
        React.createElement("h2", { style: {
                margin: `0 0 ${spacing.sm}px 0`,
                fontSize: typography.fontSizeXl,
                fontWeight: 800,
                fontFamily: typography.fontFamily,
                color: colors.white,
            } }, "Ready to try it?"),
        React.createElement("p", { style: {
                margin: `0 0 ${spacing.xl}px 0`,
                fontSize: typography.fontSizeBase,
                color: '#94A3B8',
                fontFamily: typography.fontFamily,
            }, "data-testid": "extension-cta-sentence" }, productOfferTexts(product.offer).ctaSentence),
        React.createElement("div", { style: { display: 'flex', gap: spacing.md, justifyContent: 'center', flexWrap: 'wrap' } },
            React.createElement("a", { href: product.marketplaceUrl, target: "_blank", rel: "noopener noreferrer", style: {
                    display: 'inline-block',
                    padding: `${spacing.sm + 2}px ${spacing.xl}px`,
                    background: brandGradient,
                    color: colors.white,
                    textDecoration: 'none',
                    borderRadius: radius.button,
                    fontSize: typography.fontSizeBase,
                    fontFamily: typography.fontFamily,
                    fontWeight: 700,
                    boxShadow: `0 4px 16px ${colors.primary}55`,
                } }, "Install from VS Marketplace"),
            React.createElement("a", { href: "/#contact", style: {
                    display: 'inline-block',
                    padding: `${spacing.sm + 2}px ${spacing.xl}px`,
                    border: `1.5px solid ${colors.accent}`,
                    color: colors.accent,
                    textDecoration: 'none',
                    borderRadius: radius.button,
                    fontSize: typography.fontSizeBase,
                    fontFamily: typography.fontFamily,
                    fontWeight: 600,
                } }, "Ask a question")))));
// ---------------------------------------------------------------------------
// Footer (minimal)
// ---------------------------------------------------------------------------
const MiniFooter = () => (React.createElement("footer", { style: {
        backgroundColor: colors.dark,
        padding: `${spacing.lg}px ${spacing.xl}px`,
        textAlign: 'center',
    } },
    React.createElement(Logo, { size: typography.fontSizeSm }),
    React.createElement("p", { style: {
            margin: `${spacing.sm}px 0 0 0`,
            fontSize: typography.fontSizeXs,
            color: colors.textMuted,
            fontFamily: typography.fontFamily,
        } },
        "\u00A9 ",
        new Date().getFullYear(),
        " cool-tools \u00B7",
        ' ',
        React.createElement("a", { href: "/support", style: { color: colors.textMuted } }, "Support"),
        " \u00B7",
        ' ',
        React.createElement("a", { href: "/privacy", style: { color: colors.textMuted } }, "Privacy"),
        " \u00B7",
        ' ',
        React.createElement("a", { href: "/terms", style: { color: colors.textMuted } }, "Terms"),
        " \u00B7",
        ' ',
        React.createElement("a", { href: "/imprint", style: { color: colors.textMuted } }, "Imprint"),
        " \u00B7",
        ' ',
        React.createElement("a", { href: "mailto:gerd.marinelli@cool-tools.app", style: { color: colors.textMuted } }, "Contact"))));
// ---------------------------------------------------------------------------
// Tab definitions
// ---------------------------------------------------------------------------
const TABS = [
    { id: 'quick', label: '⚡ Quick (10s)', desc: 'What it does' },
    { id: 'overview', label: '📋 Overview (90s)', desc: 'Use cases & features' },
    { id: 'deepdive', label: '🔬 Deep Dive', desc: 'How it works & compare' },
];
// ---------------------------------------------------------------------------
// Side-Nav (Desktop sticky)
// ---------------------------------------------------------------------------
const SideTabNav = ({ active, onChange }) => (React.createElement("nav", { style: {
        position: 'sticky',
        top: 80,
        width: 180,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.xs,
        alignSelf: 'flex-start',
    } }, TABS.map((tab) => (React.createElement("button", { key: tab.id, onClick: () => onChange(tab.id), style: {
        padding: `${spacing.sm}px ${spacing.md}px`,
        borderRadius: radius.card,
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        background: active === tab.id ? colors.primary + '18' : 'transparent',
        borderLeft: `3px solid ${active === tab.id ? colors.primary : 'transparent'}`,
        transition: 'all 0.15s ease',
    } },
    React.createElement("div", { style: {
            fontFamily: typography.fontFamily,
            fontSize: typography.fontSizeSm,
            fontWeight: active === tab.id ? 700 : 500,
            color: active === tab.id ? colors.primary : colors.textSecondary,
        } }, tab.label),
    React.createElement("div", { style: {
            fontFamily: typography.fontFamily,
            fontSize: typography.fontSizeXs,
            color: colors.textMuted,
            marginTop: 2,
        } }, tab.desc))))));
// ---------------------------------------------------------------------------
// Top-Tab Bar (Mobile)
// ---------------------------------------------------------------------------
const TopTabBar = ({ active, onChange }) => (React.createElement("nav", { style: {
        display: 'flex',
        gap: 0,
        borderBottom: `2px solid ${colors.border}`,
        backgroundColor: colors.white,
        position: 'sticky',
        top: 56,
        zIndex: 10,
        overflowX: 'auto',
    } }, TABS.map((tab) => (React.createElement("button", { key: tab.id, onClick: () => onChange(tab.id), style: {
        flex: 1,
        padding: `${spacing.sm}px ${spacing.md}px`,
        border: 'none',
        borderBottom: `3px solid ${active === tab.id ? colors.primary : 'transparent'}`,
        cursor: 'pointer',
        background: 'transparent',
        fontFamily: typography.fontFamily,
        fontSize: typography.fontSizeXs,
        fontWeight: active === tab.id ? 700 : 500,
        color: active === tab.id ? colors.primary : colors.textSecondary,
        whiteSpace: 'nowrap',
        transition: 'all 0.15s ease',
    } }, tab.label)))));
export const ExtensionPage = ({ extensionId }) => {
    const product = PRODUCTS.find((p) => p.id === extensionId);
    const detail = EXTENSION_DETAILS[extensionId];
    const [activeTab, setActiveTab] = useState('quick');
    const [feedbackOpen, setFeedbackOpen] = useState(false);
    const isMobile = useIsMobile();
    if (!product || !detail) {
        return (React.createElement("div", { style: pageStyle },
            React.createElement(NavBar, { links: [{ label: '← All extensions', href: '/' }] }),
            React.createElement("div", { style: { padding: spacing.xxl, textAlign: 'center', fontFamily: typography.fontFamily } },
                React.createElement("h1", null, "Extension not found"),
                React.createElement("a", { href: "/" }, "Back to cool-tools.app"))));
    }
    return (React.createElement("div", { style: pageStyle },
        React.createElement(NavBar, { links: [
                { label: '← All extensions', href: '/' },
                { label: 'Pricing', href: '/#pricing' },
                { label: 'Customer Portal', href: '/customer' },
            ] }),
        React.createElement(ExtensionHero, { product: product, detail: detail }),
        isMobile && React.createElement(TopTabBar, { active: activeTab, onChange: setActiveTab }),
        React.createElement("div", { style: {
                maxWidth: 1040,
                margin: '0 auto',
                padding: isMobile ? 0 : `${spacing.xl}px ${spacing.xl}px`,
                display: 'flex',
                gap: spacing.xl,
                alignItems: 'flex-start',
            } },
            !isMobile && React.createElement(SideTabNav, { active: activeTab, onChange: setActiveTab }),
            React.createElement("div", { style: { flex: 1, minWidth: 0 } },
                activeTab === 'quick' && (() => {
                    const hc = getExtensionHeroComponents(product.id);
                    return (React.createElement(React.Fragment, null,
                        hc && (React.createElement("section", { style: { padding: `${spacing.xl}px ${spacing.xl}px ${spacing.md}px` } },
                            React.createElement("h2", { style: { margin: `0 0 ${spacing.md}px`, fontSize: typography.fontSizeLg, fontWeight: 800, fontFamily: typography.fontFamily, color: colors.textPrimary, textAlign: 'center' } }, "See the Difference"),
                            React.createElement(BeforeAfterSlider, { before: React.createElement(hc.Before, null), after: React.createElement(hc.After, null), beforeLabel: hc.beforeLabel, afterLabel: hc.afterLabel, aspectRatio: 50 }))),
                        React.createElement(UseCasesSection, { detail: detail }),
                        React.createElement(CtaSection, { product: product })));
                })(),
                activeTab === 'overview' && (React.createElement(FeaturesSection, { product: product })),
                activeTab === 'deepdive' && (React.createElement(React.Fragment, null,
                    React.createElement(HowItWorksSection, { detail: detail }),
                    React.createElement(CtaSection, { product: product }))))),
        React.createElement(MiniFooter, null),
        React.createElement(ChatWidget, { extensionId: `cool-tools.${extensionId}`, onOpenFeedback: () => setFeedbackOpen(true) }),
        feedbackOpen && (React.createElement(FeedbackForm, { extensionId: extensionId, extensionName: product.name, onClose: () => setFeedbackOpen(false) }))));
};
